import { renderIcon } from '../../icons';
import { lockScroll, unlockScroll } from '../../scroll-lock';
import './lightbox.css';

export interface LightboxImage {
  /** Image source — http(s) URL or a data: URL. */
  url: string;
  /** Filename shown in the header. */
  name: string;
  /**
   * Optional human label for the kind of image (e.g. "Floor plan",
   * "3D render", "Photo"). When omitted, a type is derived from the
   * filename / data-URL mime (PNG, JPEG, SVG…).
   */
  type?: string;
}

export interface LightboxOptions {
  images: LightboxImage[];
  /** Which image to show first (clamped into range). */
  startIndex?: number;
}

export interface LightboxHandle {
  close: () => void;
}

const EXIT_DURATION = 150;

/** Derive a short type label from a filename or data: URL. */
function typeLabel(img: LightboxImage): string {
  if (img.type) return img.type;
  const url = img.url || '';
  if (url.startsWith('data:')) {
    const m = /^data:image\/([a-z0-9.+-]+)/i.exec(url);
    if (m) return m[1].toUpperCase().replace('JPEG', 'JPEG');
    return 'Image';
  }
  const clean = url.split('?')[0]!.split('#')[0]!;
  const ext = clean.slice(clean.lastIndexOf('.') + 1).toLowerCase();
  const map: Record<string, string> = {
    jpg: 'JPEG', jpeg: 'JPEG', png: 'PNG', webp: 'WEBP', gif: 'GIF', svg: 'SVG', avif: 'AVIF',
  };
  return map[ext] ?? (ext ? ext.toUpperCase() : 'Image');
}

/** Only real, shareable links get a Share button — never giant data: URLs. */
function isShareable(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

/**
 * Open a full-screen, in-portal image lightbox. One component for every image
 * category (plans, 3D renders, designs, photos, message attachments) so the
 * retailer never leaves the portal to view an image.
 *
 * Features: full-size view, filename + type, download, share (copy link —
 * hidden for data: URLs), prev/next within the supplied set, close on the ×,
 * backdrop click, or Escape, and ← / → keyboard navigation.
 */
export function openLightbox(opts: LightboxOptions): LightboxHandle {
  const images = (opts.images || []).filter((i) => i && i.url);
  if (images.length === 0) return { close: () => {} };

  let index = Math.min(Math.max(opts.startIndex ?? 0, 0), images.length - 1);
  const multi = images.length > 1;

  const overlay = document.createElement('div');
  overlay.className = 'tkos-lightbox';
  overlay.setAttribute('data-state', 'open');
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', 'Image viewer');
  overlay.tabIndex = -1;

  overlay.innerHTML = `
    <div class="tkos-lightbox__bar">
      <div class="tkos-lightbox__meta">
        <span class="tkos-lightbox__name" data-role="name"></span>
        <span class="tkos-lightbox__type" data-role="type"></span>
      </div>
      <div class="tkos-lightbox__tools">
        <span class="tkos-lightbox__copied" data-role="copied" hidden>Link copied</span>
        <button type="button" class="tkos-lightbox__btn" data-action="share" aria-label="Copy link to image" title="Copy link"></button>
        <button type="button" class="tkos-lightbox__btn" data-action="download" aria-label="Download image" title="Download"></button>
        <button type="button" class="tkos-lightbox__btn" data-action="close" aria-label="Close viewer" title="Close"></button>
      </div>
    </div>
    <div class="tkos-lightbox__stage" data-role="stage">
      <button type="button" class="tkos-lightbox__nav tkos-lightbox__nav--prev" data-action="prev" aria-label="Previous image"></button>
      <img class="tkos-lightbox__img" data-role="img" alt="" />
      <button type="button" class="tkos-lightbox__nav tkos-lightbox__nav--next" data-action="next" aria-label="Next image"></button>
    </div>
    <div class="tkos-lightbox__footer" data-role="footer"></div>
  `;

  const q = <T extends HTMLElement>(sel: string): T => overlay.querySelector(sel) as T;
  const nameEl = q<HTMLElement>('[data-role=name]');
  const typeEl = q<HTMLElement>('[data-role=type]');
  const imgEl = q<HTMLImageElement>('[data-role=img]');
  const footerEl = q<HTMLElement>('[data-role=footer]');
  const copiedEl = q<HTMLElement>('[data-role=copied]');
  const shareBtn = q<HTMLButtonElement>('[data-action=share]');
  const downloadBtn = q<HTMLButtonElement>('[data-action=download]');
  const closeBtn = q<HTMLButtonElement>('[data-action=close]');
  const prevBtn = q<HTMLButtonElement>('[data-action=prev]');
  const nextBtn = q<HTMLButtonElement>('[data-action=next]');

  shareBtn.appendChild(renderIcon('link', { size: 'sm' }));
  downloadBtn.appendChild(renderIcon('download', { size: 'sm' }));
  closeBtn.appendChild(renderIcon('close', { size: 'sm' }));
  prevBtn.appendChild(renderIcon('chevron-left', { size: 'md' }));
  nextBtn.appendChild(renderIcon('chevron-right', { size: 'md' }));

  if (!multi) {
    prevBtn.hidden = true;
    nextBtn.hidden = true;
  }

  let copiedTimer: number | null = null;

  function render(): void {
    const img = images[index]!;
    imgEl.src = img.url;
    imgEl.alt = img.name || 'Image';
    nameEl.textContent = img.name || 'Image';
    typeEl.textContent = typeLabel(img);
    shareBtn.hidden = !isShareable(img.url);
    footerEl.textContent = multi ? `${index + 1} of ${images.length}` : '';
    footerEl.hidden = !multi;
  }

  function go(delta: number): void {
    if (!multi) return;
    index = (index + delta + images.length) % images.length;
    render();
  }

  function doDownload(): void {
    const img = images[index]!;
    const a = document.createElement('a');
    a.href = img.url;
    a.download = img.name || 'image';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  async function doShare(): Promise<void> {
    const img = images[index]!;
    if (!isShareable(img.url)) return;
    try {
      await navigator.clipboard?.writeText(img.url);
      copiedEl.hidden = false;
      if (copiedTimer !== null) window.clearTimeout(copiedTimer);
      copiedTimer = window.setTimeout(() => { copiedEl.hidden = true; copiedTimer = null; }, 1800);
    } catch { /* clipboard blocked — silent */ }
  }

  let isOpen = false;
  let previousFocus: HTMLElement | null = null;

  const onKeyDown = (e: KeyboardEvent): void => {
    if (!isOpen) return;
    if (e.key === 'Escape') { e.preventDefault(); close(); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
  };

  const stage = q<HTMLElement>('[data-role=stage]');
  const onMouseDown = (e: MouseEvent): void => {
    // Close when the click lands on the backdrop or the empty area around the
    // image — but not on the image itself or any control.
    if (e.target === overlay || e.target === stage) close();
  };

  const onClick = (e: MouseEvent): void => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-action]');
    if (!btn || !overlay.contains(btn)) return;
    switch (btn.dataset.action) {
      case 'prev': go(-1); break;
      case 'next': go(1); break;
      case 'download': doDownload(); break;
      case 'share': void doShare(); break;
      case 'close': close(); break;
    }
  };

  function close(): void {
    if (!isOpen) return;
    isOpen = false;
    overlay.setAttribute('data-state', 'closing');
    document.removeEventListener('keydown', onKeyDown, true);
    unlockScroll();
    if (copiedTimer !== null) { window.clearTimeout(copiedTimer); copiedTimer = null; }
    window.setTimeout(() => {
      if (overlay.isConnected) overlay.remove();
      if (previousFocus && document.contains(previousFocus)) previousFocus.focus();
      previousFocus = null;
    }, EXIT_DURATION);
  }

  // Open immediately.
  render();
  isOpen = true;
  previousFocus = (document.activeElement as HTMLElement | null) ?? null;
  lockScroll();
  document.addEventListener('keydown', onKeyDown, true);
  overlay.addEventListener('mousedown', onMouseDown);
  overlay.addEventListener('click', onClick);
  document.body.appendChild(overlay);
  requestAnimationFrame(() => overlay.focus());

  return { close };
}
